import { Link } from "react-router-dom";
import {
  Lightning,
  ChatText,
  SlidersHorizontal,
  Sparkle,
  ShieldCheck,
  ClockCounterClockwise,
  Archive,
  RocketLaunch,
  Package,
  ArrowRight,
  Terminal,
  FileCode,
} from "@phosphor-icons/react";

const steps = [
  {
    n: "01",
    icon: ChatText,
    title: "Describe your goal",
    body: "Write what you want in plain English — “enforce FileVault and hide the Dock”, or “deploy Nudge with a 14-day deadline”. No scripting knowledge required.",
  },
  {
    n: "02",
    icon: SlidersHorizontal,
    title: "Choose your target",
    body: "Pick the output type (shell script, .mobileconfig profile, or a catalog package), your MDM provider, and the AI model. Every artifact is tuned to that provider's deployment model.",
  },
  {
    n: "03",
    icon: Sparkle,
    title: "AI forges the artifact",
    body: "The generator streams a production-ready, idempotent, commented artifact in real time — following Apple and MacAdmins best practices.",
  },
  {
    n: "04",
    icon: ShieldCheck,
    title: "Validate & review",
    body: "Profiles are parsed and validated (well-formed plist, correct PayloadType); scripts are sanity-checked. You see a clear pass/warning status before you ship.",
  },
  {
    n: "05",
    icon: RocketLaunch,
    title: "Save, bundle & deploy",
    body: "Copy, download, or export a per-MDM zip bundle (artifact + README with deployment steps). Save it to a project and upload to your MDM — done.",
  },
];

const features = [
  {
    icon: Terminal,
    title: "Shell scripts",
    body: "Idempotent bash tuned to Jamf, Kandji, Mosyle, Intune, Addigy or Iru deployment models.",
  },
  {
    icon: FileCode,
    title: "Configuration profiles",
    body: "Valid .mobileconfig payloads for restrictions, security and settings — validated before download.",
  },
  {
    icon: Package,
    title: "Plug & play catalog",
    body: "19+ curated open-source tools (Support App, Escrow Buddy, Nudge, Installomator…) with one-click deploy scripts.",
  },
  {
    icon: ClockCounterClockwise,
    title: "Deploy history",
    body: "Every project keeps a version timeline. Roll back to any previous artifact instantly.",
  },
  {
    icon: Archive,
    title: "Zip bundles",
    body: "Export a ready-to-hand-off package containing the artifact and a provider-specific README.",
  },
  {
    icon: ShieldCheck,
    title: "Secure by default",
    body: "Private workspace with JWT auth. Your prompts and projects are scoped to your account.",
  },
];

const providers = ["Jamf Pro", "Kandji", "Mosyle", "Microsoft Intune", "Addigy", "Iru"];

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-[#0A0A0B] grain text-[#FAFAFA]">
      <header className="border-b border-[#27272A] relative z-[2]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="hiw-logo">
            <div className="w-8 h-8 border border-[#00F0FF] flex items-center justify-center">
              <Lightning size={18} weight="fill" className="text-[#00F0FF]" />
            </div>
            <span className="font-semibold tracking-tight">MDM Forge</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="hiw-login" className="text-sm text-[#A1A1AA] hover:text-[#FAFAFA] transition-colors duration-200 px-3 py-2">
              Sign in
            </Link>
            <Link to="/register" data-testid="hiw-register" className="text-sm bg-[#00F0FF] text-[#0A0A0B] font-medium px-4 py-2 hover:bg-[#00d4e0] transition-colors duration-200">
              Get started
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-[#27272A] relative z-[2]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="inline-flex items-center gap-2 border border-[#27272A] bg-[#121214] px-3 py-1.5 mb-8 font-mono text-xs text-[#00F0FF]">
            <span className="w-1.5 h-1.5 bg-[#00FF41] rounded-full" /> HOW IT WORKS
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light max-w-3xl leading-[1.05]">
            From plain English to a
            <br />
            <span className="text-[#00F0FF]">deploy-ready artifact</span> in seconds.
          </h1>
          <p className="text-base text-[#A1A1AA] mt-6 max-w-xl font-mono leading-relaxed">
            MDM Forge turns a description into scripts, profiles and packages tuned to your Apple MDM —
            validated, versioned, and ready to upload.
          </p>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-6xl mx-auto px-6 py-24 relative z-[2]">
        <h2 className="text-2xl sm:text-3xl tracking-tight mb-12">The workflow</h2>
        <div className="border border-[#27272A]">
          {steps.map((s, i) => (
            <div
              key={s.n}
              data-testid={`hiw-step-${s.n}`}
              className={`grid grid-cols-1 md:grid-cols-[120px_56px_1fr] gap-6 p-8 hover:bg-[#121214] transition-colors duration-200 ${
                i !== steps.length - 1 ? "border-b border-[#27272A]" : ""
              }`}
            >
              <div className="font-mono text-4xl text-[#27272A] font-light">{s.n}</div>
              <div className="w-12 h-12 border border-[#27272A] flex items-center justify-center shrink-0">
                <s.icon size={22} className="text-[#00F0FF]" />
              </div>
              <div>
                <h3 className="text-xl tracking-tight mb-2">{s.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed font-mono max-w-2xl">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-[#27272A] relative z-[2]">
        <div className="max-w-6xl mx-auto px-6 py-24">
          <h2 className="text-2xl sm:text-3xl tracking-tight mb-12">What you get</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#27272A] border border-[#27272A]">
            {features.map((f) => (
              <div key={f.title} className="bg-[#0A0A0B] p-8 hover:bg-[#121214] transition-colors duration-200">
                <div className="w-10 h-10 border border-[#27272A] flex items-center justify-center mb-5">
                  <f.icon size={20} className="text-[#00F0FF]" />
                </div>
                <h3 className="text-lg tracking-tight mb-2">{f.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed font-mono">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Providers */}
      <section className="border-t border-[#27272A] relative z-[2]">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <h2 className="text-2xl sm:text-3xl tracking-tight mb-3">Works with your MDM</h2>
          <p className="text-sm text-[#A1A1AA] font-mono mb-8">
            Every artifact is generated with provider-specific deployment guidance.
          </p>
          <div className="flex flex-wrap gap-3">
            {providers.map((p) => (
              <span key={p} className="font-mono text-sm text-[#A1A1AA] border border-[#27272A] px-4 py-2.5 hover:border-[#00F0FF] hover:text-[#FAFAFA] transition-colors duration-200">
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#27272A] relative z-[2]">
        <div className="max-w-6xl mx-auto px-6 py-24 text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tighter font-light mb-4">Ready to forge?</h2>
          <p className="text-sm text-[#A1A1AA] font-mono mb-10 max-w-md mx-auto">
            Create a free account and generate your first artifact in under a minute.
          </p>
          <Link
            to="/register"
            data-testid="hiw-cta"
            className="inline-flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-6 py-3 hover:bg-[#00d4e0] transition-colors duration-200"
          >
            Start forging <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#27272A] relative z-[2]">
        <div className="max-w-6xl mx-auto px-6 py-8 font-mono text-xs text-[#71717A] flex items-center justify-between">
          <span>© 2026 MDM Forge</span>
          <Link to="/" className="hover:text-[#00F0FF] transition-colors duration-200">Home</Link>
        </div>
      </footer>
    </div>
  );
}
